












































































import { Vue, Component, Watch } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import matchRoomStore from '@/modules/match/match.store'
import authStore from '@/modules/auth/auth.store'
import tournamentStore from '@/modules/tournament/tournament.store'
import JoinableMatchGameCard from '../JoinableMatchGameCard'
import JoinableMatchJoinedTournamentCard from '../JoinableMatchJoinedTournamentCard'
import { HubConnection } from '@microsoft/signalr'
import { getRealtimeConnection } from '@/utils/realtime'
import { CancelError, CancellationToken } from '@/utils/cancel'
import { JoinableMatchEntity } from '@/api/match'
import ManageRosterDialog from '../ManageRosterDialog'
import {
  MemberJoinedTournamentsEntity,
  TournamentState
} from '@/api/tournament'

export interface JoinableMatchItem extends JoinableMatchEntity {
  cardVisible: boolean
}

@Component({
  components: {
    JoinableMatchJoinedTournamentCard,
    JoinableMatchGameCard,
    ManageRosterDialog
  }
})
export default class JoinableMatchBar extends Vue {
  matchRoomModule = getModule(matchRoomStore, this.$store)
  tournamentModule = getModule(tournamentStore, this.$store)
  authModule = getModule(authStore, this.$store)
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()

  joinableMatches: JoinableMatchEntity[] | null = []
  timer!: NodeJS.Timer

  // Selected Item Id, can be tournamentId or matchGameId
  selectedId: string = ''
  selectedMatchGameId: string = ''
  isManageRosterDialogVisible: boolean = false

  /* Vuex State */
  get joinedTournaments(): MemberJoinedTournamentsEntity[] {
    return this.tournamentModule.joinedTournaments
  }

  // filter away all the tournaments with joinable match to prevent icon duplication
  get visibleTournaments() {
    let matchTournamentIds: string[] = []
    if (this.joinableMatches && this.joinableMatches.length) {
      this.joinableMatches.forEach(match => {
        if (match.relatedTournament) {
          matchTournamentIds.push(match.relatedTournament.tournamentId)
        }
      })
    }
    // let matchTournamentIds =
    //   this.joinableMatches && this.joinableMatches.length
    //     ? this.joinableMatches.map(
    //         match => match.relatedTournament.tournamentId
    //       )
    //     : []

    return this.joinedTournaments.filter(
      tournament =>
        !matchTournamentIds.includes(tournament.tournamentId) &&
        ![TournamentState.Completed, TournamentState.Cancelled].includes(
          tournament.state
        ) &&
        tournament.hasUpcomingMatch
    )
  }

  /* Getters */
  get memberJoinableMatchRooms(): JoinableMatchEntity[] | null {
    return this.matchRoomModule.memberJoinableMatchRooms
  }

  @Watch('memberJoinableMatchRooms')
  onMemberJoinableMatchRoomsChanged(
    joinableMatches: JoinableMatchEntity[] | null
  ) {
    if (!joinableMatches) {
      this.joinableMatches = []
      return
    }

    this.joinableMatches = joinableMatches
  }

  @Watch('selectedId')
  onSelectedIdChanged(id: string) {
    if (id && id.length > 0) {
      this.selectedMatchGameId = id
    }
  }

  /* Methods */
  toggleCardVisible(id: string) {
    if (this.selectedId === id) {
      this.selectedId = ''
    } else {
      this.selectedId = id
    }
  }

  showConfirmRosterDialog(joinableMatchItem: JoinableMatchItem) {
    this.selectedId = joinableMatchItem.matchGameId
    this.isManageRosterDialogVisible = true
  }

  enterMatchRoom(joinableMatchItem: JoinableMatchItem) {
    this.selectedId = '' // this will hide the card
    this.selectedMatchGameId = joinableMatchItem.matchGameId
    this.enterSelectedMatchRoom()
  }

  enterSelectedMatchRoom() {
    this.isManageRosterDialogVisible = false
    const selectedMatchGameId = this.selectedMatchGameId
    this.$router.push({
      name: 'Match',
      params: {
        matchGameId: selectedMatchGameId
      }
    })
  }

  async updateJoinableMatchGames() {
    const joinableMatches = this.matchRoomModule.joinableMatchRooms
      ? this.matchRoomModule.joinableMatchRooms
      : []
    await this.matchRoomModule.getMemberJoinableMatchRooms()

    if (
      !this.matchRoomModule.joinableMatchRooms ||
      this.selectedId.length > 0
    ) {
      return
    }

    const newMatches = this.matchRoomModule.joinableMatchRooms.filter(
      match => !joinableMatches.find(m => m.matchGameId === match.matchGameId)
    )

    if (!newMatches[0]) {
      return
    }

    // pop the first new matches
    this.selectedId = newMatches[0].matchGameId

    if (window.ipcRenderer) {
      window.ipcRenderer.send('flash-window')
    }
  }

  async fetchData() {
    await this.updateJoinedTournaments()
    await this.updateJoinableMatchGames()
  }

  async closeManageRosterDialog() {
    this.isManageRosterDialogVisible = false
    await this.fetchData()
  }

  // Lifecycle hooks
  async created() {
    this.timer = setInterval(this.fetchData, 60 * 1000)
    await this.fetchData()

    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(this.$t('JoinableMatchBar.FailConnectServerMessage'))
      console.error(err)
    }

    console.log('subscribed')
    this.realtimeConnection.on(
      'updateJoinableMatchGames',
      this.updateJoinableMatchGames
    )
    this.realtimeConnection.on(
      'tournamentTeamCreated',
      this.updateJoinedTournaments
    )
    this.realtimeConnection.on(
      'tournamentTeamRegistrationConfirmed',
      this.updateJoinedTournaments
    )
    this.realtimeConnection.on('matchComplete', this.updateJoinableMatchGames)
    this.realtimeConnection.on('matchRemoved', this.updateJoinableMatchGames)
  }

  async updateJoinedTournaments() {
    await this.tournamentModule.updateJoinedTournaments()
  }

  async beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }

    this.realtimeConnectionCancellationToken.cancel()

    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'updateJoinableMatchGames',
        this.updateJoinableMatchGames
      )
      this.realtimeConnection.off(
        'tournamentTeamRegistrationConfirmed',
        this.updateJoinedTournaments
      )
      this.realtimeConnection.off(
        'tournamentTeamCreated',
        this.updateJoinedTournaments
      )
      this.realtimeConnection.off(
        'matchComplete',
        this.updateJoinableMatchGames
      )
      this.realtimeConnection.off('matchRemoved', this.updateJoinableMatchGames)
    }
  }
}
